import React from "react";

interface PlanInfoTooltipProps {
  planValue: string;
}

const planDetails: Record<
  string,
  {
    title: string;
    coreConcept: string;
    accent: string;
    rows: { category: string; purpose: string }[];
  }
> = {
  Single: {
    title: "Single-Stamp ",
    coreConcept:
      "Captured a verified, one-time record of vehicle condition and more...",
    accent: "#2D65F2",
    rows: [
      {
        category: "Vehicle Marketplaces",
        purpose:
          "Authenticate vehicle identity, mileage, and condition prior to listing",
      },
      {
        category: "Pre-Purchase / Buyers",
        purpose:
          "Capture an independent, verified vehicle snapshot before buying",
      },
      {
        category: "Dealerships",
        purpose: "Log baseline vehicle condition prior to customer test drives",
      },
      {
        category: "Vehicle Transport",
        purpose:
          "Document initial vehicle state before physical custody begins",
      },
      {
        category: "Insurance Companies",
        purpose:
          "Establish baseline condition prior to issuing coverage or policy events",
      },
      {
        category: "Fleet Companies",
        purpose:
          "Audit and record vehicle condition upon intake into the fleet",
      },
      {
        category: "Rental Companies",
        purpose: "Benchmark vehicle condition before releasing to the customer",
      },
      {
        category: "Auctions",
        purpose: "Generate a certified vehicle condition report prior to sale",
      },
      {
        category: "Service / Repair Shops",
        purpose:
          "Record pre-service condition to prevent fraudulent damage claims",
      },
      {
        category: "Vehicle Financing / Leasing",
        purpose:
          "Verify vehicle identity and baseline condition at final delivery",
      },
    ],
  },
  Dual: {
    title: "Dual-Stamp",
    coreConcept:
      "Compared two inspection points (before & after) to track usage, liability, and changes over time.",
    accent: "#0f766e",
    rows: [
      {
        category: "Rental / Car Sharing",
        purpose:
          "Track driver identity, location, timeline, and condition changes between check-in and check-out",
      },
      {
        category: "Vehicle Transport",
        purpose:
          "Monitor vehicle custody from pickup to drop-off and pinpoint exact moments of change",
      },
      {
        category: "Dealership Loaners",
        purpose:
          "Account for mileage, usage, and wear throughout the loaner period",
      },
      {
        category: "Lease Vehicles",
        purpose:
          "Benchmark end-of-lease condition and mileage against the original delivery state",
      },
      {
        category: "Insurance Claims",
        purpose:
          "Compare pre-loss vs. post-loss state to verify timing, location, and loss integrity",
      },
      {
        category: "Service / Repair",
        purpose:
          "Document pre-service condition and verify repair completion state upon release",
      },
    ],
  },
  Invoice: {
    title: "Invoice & Settlement ",
    coreConcept:
      "Convert verified condition (usages, damage and more) into financial settlement.",
    accent: "#9a3412",
    rows: [
      {
        category: "Rental Companies",
        purpose:
          "Reconcile return damage, excess mileage, and reconditioning fees",
      },
      {
        category: "Leasing Companies",
        purpose:
          "Calculate excess mileage, wear-and-tear, and end-of-lease repair costs",
      },
      {
        category: "Dealerships",
        purpose:
          "Bill customers accurately for loaner overages, fuel, or return damage",
      },
      {
        category: "Car-Sharing Companies",
        purpose:
          "Settle trip-specific damage, excess usage, and cleaning charges",
      },
      {
        category: "Transport Companies",
        purpose:
          "Resolve pickup-to-delivery damage disputes and repair liabilities",
      },
      {
        category: "Insurance Companies",
        purpose:
          "Assess pre-loss vs. post-loss condition to calculate true claim payouts",
      },
    ],
  },
};

const PlanInfoTooltip = ({ planValue }: PlanInfoTooltipProps) => {
  const data = planDetails[planValue];

  if (!data) {
    return (
      <div className="px-4 py-3 text-[13px] text-[#6F6464]">
        No information available
      </div>
    );
  }

  // Split rows into two equal columns
  const mid = Math.ceil(data.rows.length / 2);
  const leftColumn = data.rows.slice(0, mid);
  const rightColumn = data.rows.slice(mid);

  return (
    <div className="w-fit text-left overflow-hidden">
      {/* Header */}
      <div
        className="px-4 py-3 border-b border-[#E8EEF9]"
        style={{ borderLeft: `4px solid ${data.accent}` }}
      >
        <p className="text-[11px] font-semibold tracking-wider text-[#6B7280] uppercase mb-1">
          Inspection Plan
        </p>
        <h3 className="text-[15px] font-bold text-[#111827] leading-snug">
          {data.title}
        </h3>
      </div>

      {/* Core Concept */}
      <div className="px-4 py-3 bg-[#F5F9FC] border-b border-[#E8EEF9]">
        <p className="text-[12px] text-[#374151] leading-relaxed">
          {data.coreConcept}
        </p>
      </div>

      {/* Two-column layout (no scroll) */}
      <div className="px-4 py-3.5">
        <p className="text-[11px] font-semibold tracking-wider text-[#6B7280] uppercase mb-3">
          Best suited for
        </p>

        <div className="grid grid-cols-2 gap-x-5 gap-y-0">
          {/* LEFT COLUMN */}
          <div className="space-y-3">
            {leftColumn.map((row, idx) => (
              <div key={idx} className="flex gap-2.5">
                <div
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: data.accent }}
                />
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#111827] leading-tight">
                    {row.category}
                  </p>
                  <p className="text-[12px] text-[#6B7280] leading-snug mt-0.5">
                    {row.purpose}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-3">
            {rightColumn.map((row, idx) => (
              <div key={idx} className="flex gap-2.5">
                <div
                  className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ backgroundColor: data.accent }}
                />
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#111827] leading-tight">
                    {row.category}
                  </p>
                  <p className="text-[12px] text-[#6B7280] leading-snug mt-0.5">
                    {row.purpose}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanInfoTooltip;
